/**
 * TradeFinder push alert rules (engine side, utt-engine flow_signals/alerts.rs).
 *
 * - GET /api/flow-alerts/settings -> FlowAlertSettings (defaults when unset)
 * - PUT /api/flow-alerts/settings (full object) -> the saved, clamped settings
 * - socket `tradefinder-alert-settings` -> FlowAlertSettings after any save
 *
 * Only the production engine evaluates and sends (the phone app connects to
 * it); staging shares the same Redis, so editing here from either is fine.
 * The browser chime applies the same rules locally (evaluateAlert).
 */
import { create } from 'zustand';
import { getApiBaseUrl } from '../config/ports';
import { wsService } from '../services/WebSocketService';
import { useMarketStore } from './marketStore';
import { normalizeAlertSettings, type FlowAlertSettings } from '../components/TradeFinder/tradeFinderUx';

export const ALERT_SETTINGS_EVENT = 'tradefinder-alert-settings';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

interface FlowAlertsState {
  settings: FlowAlertSettings | null;
  status: LoadState;
  error: string | null;
  fetch: () => Promise<void>;
  save: (next: FlowAlertSettings) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** Apply a pushed or fetched object. */
  apply: (raw: unknown) => void;
  /** Reference-counted socket listener. */
  listen: () => () => void;
}

let listeners = 0;
let unsubConn: (() => void) | null = null;
const onPush = (m: unknown) => useFlowAlertsStore.getState().apply(m);

/** (Re)bind: a forced reconnect builds a new socket. */
function bind(): void {
  if (!wsService.isConnected()) return; // bound on the next 'connected'
  wsService.off(ALERT_SETTINGS_EVENT, onPush);
  wsService.on(ALERT_SETTINGS_EVENT, onPush);
}

async function bodyError(res: Response): Promise<string> {
  try {
    const j = await res.json();
    if (j && typeof j === 'object' && typeof (j as { error?: unknown }).error === 'string') return (j as { error: string }).error;
  } catch { /* not json */ }
  return `HTTP ${res.status}`;
}

export const useFlowAlertsStore = create<FlowAlertsState>((set, get) => ({
  settings: null,
  status: 'idle',
  error: null,

  apply: (raw) => {
    if (!raw || typeof raw !== 'object') return;
    set({ settings: normalizeAlertSettings(raw), status: 'ready', error: null });
  },

  fetch: async () => {
    if (get().status === 'loading') return;
    set({ status: get().settings ? 'ready' : 'loading' });
    try {
      const res = await fetch(`${getApiBaseUrl()}/flow-alerts/settings`);
      if (!res.ok) throw new Error(await bodyError(res));
      get().apply(await res.json());
    } catch (e) {
      set({ status: 'error', error: e instanceof Error ? e.message : String(e) });
    }
  },

  save: async (next) => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/flow-alerts/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) return { ok: false, error: await bodyError(res) };
      get().apply(await res.json());
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
  },

  listen: () => {
    listeners += 1;
    if (listeners === 1) {
      bind();
      let prev = useMarketStore.getState().connectionStatus;
      unsubConn = useMarketStore.subscribe((st) => {
        if (st.connectionStatus === prev) return;
        prev = st.connectionStatus;
        if (prev === 'connected') {
          bind();
          void get().fetch(); // a save may have landed while we were away
        }
      });
    }
    let done = false;
    return () => {
      if (done) return;
      done = true;
      listeners -= 1;
      if (listeners > 0) return;
      unsubConn?.();
      unsubConn = null;
      if (wsService.isConnected()) wsService.off(ALERT_SETTINGS_EVENT, onPush);
    };
  },
}));
